
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { doc } from "firebase/firestore";
import { db, auth } from "@/services/firebaseService";
import { createAndAttachCampPhotoFromStorageUpload } from "@/services/campPhotoImportService";
import { uploadPhoto } from "@/services/photoStorageService";
import { useAuth } from "@/context/AuthContext";

const formSchema = z.object({
  photoReference: z.string().min(1, "Photo credit is required"),
  caption: z.string().max(1000, "Caption must be 1,000 characters or less").optional().nullable(),
  altText: z.string().max(1000, "Alt text must be 1,000 characters or less").optional().nullable(),
  yOffset: z.number().min(-1).max(1).default(0),
  permissionStatus: z.enum(["seeking", "granted"]),
  permissionNote: z.string().optional().nullable()
});

export type PhotoUploadFormValues = z.infer<typeof formSchema>;

export const usePhotoUploadForm = (
  locationId: string,
  onSuccess: () => void,
  organizationName: string
) => {
  const { currentUser, userProfile, refreshAuthToken } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [organizationLoaded, setOrganizationLoaded] = useState(false);
  
  const form = useForm<PhotoUploadFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      photoReference: organizationName || "",
      caption: "",
      altText: "",
      yOffset: 0,
      permissionStatus: userProfile?.role === "camp_user" ? "granted" : "seeking",
      permissionNote: userProfile?.role === "camp_user" ? "Uploaded by camp user" : "",
    }
  });

  useEffect(() => {
    if (organizationName) {
      form.setValue("photoReference", organizationName);
      setOrganizationLoaded(true);
    }
    
    if (userProfile?.role === "camp_user") {
      form.setValue("permissionStatus", "granted");
      form.setValue("permissionNote", "Uploaded by camp user");
    }
  }, [organizationName, userProfile, form]);

  const handleSubmit = async (
    values: PhotoUploadFormValues,
    file: File,
    organizationId: string,
    options?: { outsidePermission?: boolean; permissionExplanation?: string }
  ) => {
    console.log("handleSubmit called with:", {
      values,
      fileName: file?.name,
      organizationId
    });
    
    setError(null);
    
    // Get the current Firebase authentication instance and refresh token
    if (!auth.currentUser) {
      // If we don't have a current user, try to refresh the page auth state
      await auth.authStateReady()
        .catch(err => {
          console.error("Failed to wait for auth state:", err);
        });
    }
    
    // Refresh auth token before proceeding
    await refreshAuthToken();
    
    // Verify Firebase authentication after ensuring auth is ready
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      const errorMsg = "Firebase authentication required. Please refresh the page and log in again.";
      console.error(errorMsg);
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    
    // Output token information for debugging
    try {
      const tokenResult = await firebaseUser.getIdTokenResult();
      console.log("Token claims before upload:", tokenResult.claims);
      console.log("Token expiration:", new Date(tokenResult.expirationTime));
    } catch (err) {
      console.error("Failed to get token details:", err);
    }
    
    // Validate required data before proceeding
    if (!file) {
      const errorMsg = "Please select a photo to upload";
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    
    if (!currentUser) {
      const errorMsg = "You need to be logged in to upload photos";
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    
    if (!organizationId) {
      const errorMsg = "Organization information is missing";
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    
    if (!locationId) {
      const errorMsg = "Location information is missing";
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    
    setUploading(true);
    console.log("Starting upload process for:", file.name);
    console.log("Organization ID:", organizationId);
    console.log("Firebase auth state:", !!firebaseUser, "UID:", firebaseUser.uid);
    
    try {
      // Refresh the token first
      await refreshAuthToken();
      
      // Make sure to pass the arguments in the correct order: file, orgId
      const result = uploadPhoto(
        file, // First parameter: file
        organizationId, // Second parameter: orgId
        (progress) => {
          console.log("Upload progress update:", progress);
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Upload error:", error);
          setError(`Failed to upload photo: ${error.message}`);
          toast.error(`Failed to upload photo: ${error.message}`);
          setUploading(false);
        },
        async (downloadUrl, filePath) => {
          try {
            console.log("Upload successful, processing photo data");
            const now = new Date();
            const formatEasternISO = (dt: Date) => {
              const parts = new Intl.DateTimeFormat('en-US', {
                timeZone: 'America/New_York',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
              }).formatToParts(dt);
              const get = (t: string) => parts.find(p => p.type === t)?.value || '';
              return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`;
            };
            const getEasternYMD = (dt: Date) => {
              const p = new Intl.DateTimeFormat('en-US', {
                timeZone: 'America/New_York',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }).formatToParts(dt);
              const year = p.find(x => x.type === 'year')?.value || '';
              const monthNum = parseInt(p.find(x => x.type === 'month')?.value || '0', 10);
              const dayNum = parseInt(p.find(x => x.type === 'day')?.value || '0', 10);
              return { year, monthNum, dayNum };
            };
            const userName = `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() || (firebaseUser.displayName || 'Unknown User');
            const userEmail = firebaseUser.email || 'unknown@email';
            const ipAddress = (await fetch('https://api.ipify.org?format=json').then(r => r.json()).catch(() => ({ ip: 'unknown' }))).ip;
            const tokenResult = await firebaseUser.getIdTokenResult();
            const locationAccessGroupId =
              userProfile?.locationAccessGroupId ||
              (typeof (tokenResult.claims as any)?.locationAccessGroupId === "string"
                ? ((tokenResult.claims as any).locationAccessGroupId as string)
                : undefined);

            const easternIso = formatEasternISO(now);
            const { year, monthNum, dayNum } = getEasternYMD(now);
            const monthNames = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];
            const prettyDate = `${monthNames[Math.max(1, monthNum) - 1]} ${dayNum}, ${year}`;

            const usingOutsidePermission = !!options?.outsidePermission;
            const explanationText = (options?.permissionExplanation || "").trim();
            const isCanva = (values.photoReference || "").trim() === "Canva";

            const permissionInternalNote = isCanva
              ? `${userName} (${userEmail}, ${currentUser.uid}) at ${easternIso} Eastern from IP address ${ipAddress} asserts photo is from Canva.`
              : (usingOutsidePermission
                ? `${userName} (${userEmail}, ${currentUser.uid}) at ${easternIso} Eastern from IP address ${ipAddress} described permission we received outside the system as ${explanationText || "(no explanation provided)"}`
                : `${userName} (${userEmail}, ${currentUser.uid}) at ${easternIso} Eastern from IP address ${ipAddress} clicked 'I am able to grant permission to use this image, and I give KidsBooked permission to use this image in accordance with the Terms of Use.'`);
            const permissionNote = isCanva
              ? ""
              : (usingOutsidePermission
                ? explanationText
                : `Permission granted by ${userName} on ${prettyDate} in the photo upload form`);

            await createAndAttachCampPhotoFromStorageUpload({
              locationId,
              organizationId,
              storagePath: filePath,
              downloadUrl,
              yOffset: values.yOffset || 0,
              caption: values.caption || "",
              altText: values.altText || "",
              attribution: {
                photoReference: values.photoReference || organizationName,
                permissionStatus: "granted",
                permissionNote,
                permissionInternalNote,
              },
              locationAccessGroupId: locationAccessGroupId || undefined,
            });
            
            toast.success("Photo uploaded successfully");
            onSuccess();
          } catch (error) {
            console.error("Error creating photo record:", error);
            setError("Failed to create photo record");
            toast.error("Failed to create photo record");
          } finally {
            setUploading(false);
          }
        }
      );
      
      if (result === null) {
        // This means the upload didn't even start due to validation errors
        setUploading(false);
      }
    } catch (error) {
      console.error("Upload setup failed:", error);
      setError(`Failed to setup upload: ${error instanceof Error ? error.message : String(error)}`);
      toast.error(`Failed to setup upload: ${error instanceof Error ? error.message : String(error)}`);
      setUploading(false);
    }
  };

  return {
    form,
    uploading,
    uploadProgress,
    handleSubmit,
    isAdmin: userProfile?.role === "admin_user",
    error,
    organizationLoaded
  };
};
